import { useEffect, useState } from "react";
import { useAuth } from "@/context/AuthContext";
import { useNavigate } from "react-router-dom";
import api, { formatApiError } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Settings2, Save, ArrowLeft, Building2, KeyRound, Percent, Phone, ImageIcon, Loader2 } from "lucide-react";
import { toast } from "sonner";
import Logo from "@/components/Logo";

export default function Settings() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [form, setForm] = useState(null);
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    if (user && !["admin", "super_admin"].includes(user.role)) {
      navigate("/", { replace: true });
      return;
    }
    (async () => {
      try {
        const { data } = await api.get("/settings");
        setForm({
          company_name: data.company_name || "",
          pix_key: data.pix_key || "",
          whatsapp_company: data.whatsapp_company || "",
          default_interest_rate: data.default_interest_rate ?? 20,
          default_daily_late_fee: data.default_daily_late_fee ?? "",
          logo_url: data.logo_url || "",
          plan_price_mensal: data.plan_price_mensal ?? 30,
          plan_price_semestral: data.plan_price_semestral ?? 150,
          plan_price_anual: data.plan_price_anual ?? 300,
        });
      } catch (e) {
        toast.error(formatApiError(e));
      }
    })();
  }, [user, navigate]);

  const set = (k) => (e) => {
    const v = e?.target ? e.target.value : e;
    setForm((f) => ({ ...f, [k]: v }));
  };

  const save = async () => {
    setBusy(true);
    try {
      const payload = {
        company_name: form.company_name.trim(),
        pix_key: form.pix_key.trim(),
        whatsapp_company: form.whatsapp_company.trim(),
        default_interest_rate: parseFloat(form.default_interest_rate) || 0,
        default_daily_late_fee: form.default_daily_late_fee !== "" ? parseFloat(form.default_daily_late_fee) : null,
        logo_url: form.logo_url.trim(),
        plan_price_mensal: parseFloat(form.plan_price_mensal) || 30,
        plan_price_semestral: parseFloat(form.plan_price_semestral) || 150,
        plan_price_anual: parseFloat(form.plan_price_anual) || 300,
      };
      await api.patch("/admin/settings", payload);
      toast.success("Configurações salvas");
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setBusy(false);
    }
  };

  if (!form) {
    return (
      <div className="min-h-screen flex items-center justify-center text-slate-400">
        <Loader2 className="w-6 h-6 animate-spin" />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#060A12] text-slate-100" data-testid="settings-page">
      <header
  className="fixed top-0 left-0 right-0 z-50 backdrop-blur-xl bg-[#060A12]/95 border-b border-slate-800/80"
  style={{ paddingTop: "env(safe-area-inset-top)" }}
>
        <div className="max-w-4xl mx-auto px-4 md:px-8 h-16 flex items-center justify-between gap-2">
          <div className="flex items-center gap-3 min-w-0">
            <Logo size={34} />
            <div className="min-w-0">
              <div className="text-white font-extrabold tracking-tight" style={{ fontFamily: "Outfit" }}>
                Configurações
              </div>
              <div className="text-[10px] uppercase tracking-widest text-slate-400">
                Administrador
              </div>
            </div>
          </div>
          <Button
            variant="ghost"
            size="sm"
            className="text-slate-300"
            onClick={() => navigate("/")}
            data-testid="settings-back-btn"
          >
            <ArrowLeft className="w-4 h-4 mr-2" /> Voltar
          </Button>
        </div>
      </header>

      <main className="max-w-4xl mx-auto px-3 sm:px-4 md:px-8 pt-24 sm:pt-28 pb-6 sm:pb-8 space-y-5 sm:space-y-6">
        <div>
          <div className="text-[10px] sm:text-xs uppercase tracking-widest text-blue-400 font-bold mb-2">
            Preferências gerais
          </div>
          <h1 className="text-3xl sm:text-4xl font-extrabold tracking-tight" style={{ fontFamily: "Outfit" }}>
            Ajustes da plataforma
          </h1>
          <p className="text-slate-400 mt-1 text-sm">
            Alterações se aplicam a novos empréstimos, propostas e recibos. Não afetam contratos já cadastrados.
          </p>
        </div>

        <div className="glass-card p-4 sm:p-6 space-y-5">
          <div>
            <Label className="text-slate-300 flex items-center gap-2 mb-1.5">
              <Building2 className="w-4 h-4" /> Nome da empresa
            </Label>
            <Input
              value={form.company_name}
              onChange={set("company_name")}
              className="bg-slate-900/60 border-slate-700"
              data-testid="settings-company"
            />
          </div>
          <div className="grid sm:grid-cols-2 gap-4">
            <div>
              <Label className="text-slate-300 flex items-center gap-2 mb-1.5">
                <KeyRound className="w-4 h-4" /> Chave PIX
              </Label>
              <Input
                value={form.pix_key}
                onChange={set("pix_key")}
                placeholder="Ex: 22988791498"
                className="bg-slate-900/60 border-slate-700 font-mono"
                data-testid="settings-pix"
              />
              <div className="text-[11px] text-slate-500 mt-1">Usada nos planos e recibos.</div>
            </div>
            <div>
              <Label className="text-slate-300 flex items-center gap-2 mb-1.5">
                <Phone className="w-4 h-4" /> WhatsApp da empresa
              </Label>
              <Input
                value={form.whatsapp_company}
                onChange={set("whatsapp_company")}
                placeholder="Ex: 22988791498"
                className="bg-slate-900/60 border-slate-700 font-mono"
                data-testid="settings-wa"
              />
            </div>
          </div>

          <div className="grid sm:grid-cols-2 gap-4">
            <div>
              <Label className="text-slate-300 flex items-center gap-2 mb-1.5">
                <Percent className="w-4 h-4" /> Juros padrão (% ao mês)
              </Label>
              <Input
                type="number"
                step="0.01"
                min="0"
                value={form.default_interest_rate}
                onChange={set("default_interest_rate")}
                className="bg-slate-900/60 border-slate-700 font-mono"
                data-testid="settings-rate"
              />
            </div>
            <div>
              <Label className="text-slate-300 flex items-center gap-2 mb-1.5">
                Juros diário de atraso padrão (R$/dia)
              </Label>
              <Input
                type="number"
                step="0.01"
                min="0"
                placeholder="Vazio = calcular automático"
                value={form.default_daily_late_fee}
                onChange={set("default_daily_late_fee")}
                className="bg-slate-900/60 border-slate-700 font-mono"
                data-testid="settings-late-fee"
              />
            </div>
          </div>

          <div>
            <Label className="text-slate-300 flex items-center gap-2 mb-1.5">
              <ImageIcon className="w-4 h-4" /> URL do logo (opcional)
            </Label>
            <Input
              value={form.logo_url}
              onChange={set("logo_url")}
              placeholder="https://..."
              className="bg-slate-900/60 border-slate-700"
              data-testid="settings-logo"
            />
          </div>

          <div className="pt-4 border-t border-slate-800/60">
            <div className="text-[10px] uppercase tracking-widest text-blue-400 font-bold mb-3">
              Preços dos planos (negociáveis)
            </div>
            <div className="grid sm:grid-cols-3 gap-4">
              <div>
                <Label className="text-slate-300 mb-1.5 block">Mensal · 30 dias</Label>
                <Input
                  type="number"
                  step="0.01"
                  min="0"
                  value={form.plan_price_mensal}
                  onChange={set("plan_price_mensal")}
                  className="bg-slate-900/60 border-slate-700 font-mono"
                  data-testid="settings-plan-mensal"
                />
              </div>
              <div>
                <Label className="text-slate-300 mb-1.5 block">Semestral · 180 dias</Label>
                <Input
                  type="number"
                  step="0.01"
                  min="0"
                  value={form.plan_price_semestral}
                  onChange={set("plan_price_semestral")}
                  className="bg-slate-900/60 border-slate-700 font-mono"
                  data-testid="settings-plan-semestral"
                />
              </div>
              <div>
                <Label className="text-slate-300 mb-1.5 block">Anual · 365 dias</Label>
                <Input
                  type="number"
                  step="0.01"
                  min="0"
                  value={form.plan_price_anual}
                  onChange={set("plan_price_anual")}
                  className="bg-slate-900/60 border-slate-700 font-mono"
                  data-testid="settings-plan-anual"
                />
              </div>
            </div>
            <div className="text-[11px] text-slate-500 mt-2">
              Alterar aqui reflete instantaneamente na página <strong>Escolha seu plano</strong> vista pelos usuários.
            </div>
          </div>

          <div className="pt-4 border-t border-slate-800/60 flex justify-stretch sm:justify-end">
            <Button
              onClick={save}
              disabled={busy}
              className="w-full sm:w-auto bg-blue-600 hover:bg-blue-500 text-white"
              data-testid="settings-save"
            >
              {busy ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Save className="w-4 h-4 mr-2" />}
              Salvar alterações
            </Button>
          </div>
        </div>
      </main>
    </div>
  );
}
